'use client';

import { useState, useEffect } from 'react';
import { ReadonlyURLSearchParams } from 'next/navigation';
import { getContentParams, buildPlayStoreUrl, resolveUtmTrackingParams } from '@/lib/url-utils';
import { trackAnalyticsEvent } from '@/lib/analytics';
import { useSettings } from '@/hooks/useSettings';
import { getAdjustAttribution } from '@/lib/adjust';

/**
 * Pre-resolves the Play Store URL (with Adjust attribution + settings mapping)
 * as early as possible so `redirectToPlayStore()` can fire instantly.
 *
 * Also fires all required analytics/pixel events at redirect time.
 */
export const usePlayStoreRedirect = (searchParams: ReadonlyURLSearchParams) => {
  const { data: settingsData } = useSettings();
  const { showId, episodeNum, linkToken } = getContentParams(searchParams);
  const utmTrackingParams = resolveUtmTrackingParams(searchParams);

  const [playStoreUrl, setPlayStoreUrl] = useState<string>(() =>
    buildPlayStoreUrl(showId, episodeNum, linkToken)
  );

  // Refine URL with Adjust attribution as soon as settings are ready
  useEffect(() => {
    const resolveUrl = async () => {
      try {
        const attribution = await getAdjustAttribution();
        if (!attribution?.tracker_token) return;

        const mapping = settingsData?.setting?.adjustWebCampaignReference;
        const mappedToken = mapping ? mapping[attribution.tracker_token] : null;

        if (mappedToken) {
          setPlayStoreUrl(`https://alrighttv.go.link/${mappedToken}`);
        } else if (!linkToken) {
          setPlayStoreUrl(buildPlayStoreUrl(showId, episodeNum, attribution.tracker_token));
        }
      } catch (error) {
        console.error('Error resolving Adjust attribution for Play Store URL:', error);
      }
    };

    if (settingsData) {
      resolveUrl();
    }
  }, [settingsData, showId, episodeNum, linkToken]);

  const redirectToPlayStore = () => {
    // Quantcast free trial conversion pixel
    (window as unknown as { _qevents: object[] })._qevents =
      (window as unknown as { _qevents: object[] })._qevents || [];
    (window as unknown as { _qevents: object[] })._qevents.push({
      qacct: 'p-7mgqFvP7syTuE',
      labels: '_fp.event.Free Trial Conversion',
      event: 'refresh',
    });

    trackAnalyticsEvent('WEB_DOWNLOAD_PAGE_VISITED', utmTrackingParams);
    trackAnalyticsEvent('DOWNLOAD_INITIATED', {
      store: 'google_play',
      showId: showId ?? '',
      episodeNum: episodeNum ?? '',
      ...utmTrackingParams,
      playStoreUrl: playStoreUrl
    });

    window.location.href = playStoreUrl;
  };

  return { redirectToPlayStore };
};
